/* eslint-disable import/first -- React 19 DevTools patch must run before imports */
// Comprehensive patch for React DevTools semver error with React 19
// This must run before React loads - imports are intentionally after this patch
if (typeof window !== 'undefined') {
  // Global error handler to catch and suppress benign ResizeObserver and semver notifications
  const originalErrorHandler = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (
      message &&
      typeof message === 'string' &&
      (message.includes('ResizeObserver') ||
        message.includes('semver') ||
        message.includes('Invalid argument'))
    ) {
      return true; // Suppress benign notification
    }
    if (originalErrorHandler) {
      return originalErrorHandler.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  window.addEventListener('error', (event) => {
    if (
      event.message &&
      typeof event.message === 'string' &&
      (event.message.includes('ResizeObserver') ||
        event.message.includes('semver') ||
        event.message.includes('Invalid argument'))
    ) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  });
    
  // Also catch unhandled promise rejections
  const originalUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = function(event) {
    if (event.reason && 
        (event.reason.message?.includes('semver') || 
         event.reason.message?.includes('Invalid argument'))) {
      event.preventDefault();
      return;
    }
    if (originalUnhandledRejection) {
      return originalUnhandledRejection.call(this, event);
    }
  };
  
  // Initialize DevTools hook early
  if (!window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__ = {
      renderers: new Map(),
      supportsFiber: true,
    };
  }

  const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;

  if (!hook.renderers) {
    hook.renderers = new Map();
  }

  // Helper to wrap renderer with Proxy that always returns valid version
  const proxyRenderer = (renderer) => {
    if (!renderer) return renderer;
    return new Proxy(renderer, {
      get(target, prop) {
        if (prop === 'version') {
          const v = target.version;
          if (!v || String(v).trim() === '') return '19.0.0';
          return v;
        }
        return target[prop];
      }
    });
  };

  // Wrap renderers Map with Proxy to intercept version reads (React 19 fix)
  const origSet = hook.renderers.set?.bind(hook.renderers);
  const origGet = hook.renderers.get?.bind(hook.renderers);
  
  if (origSet) {
    hook.renderers.set = function (id, renderer) {
      return origSet(id, proxyRenderer(renderer));
    };
  }

  // Retroactively proxy any existing renderers already in the Map
  if (origGet && hook.renderers.size > 0) {
    const existingEntries = Array.from(hook.renderers.entries());
    hook.renderers.clear();
    existingEntries.forEach(([id, renderer]) => {
      hook.renderers.set(id, renderer); // Will use our patched set
    });
  }

  const ensureRendererVersion = (r) => {
    if (r && (!r.version || String(r.version).trim() === '')) r.version = '19.0.0';
    if (r?.findFiberByHostInstance && !r.findFiberByHostInstance.version) {
      r.findFiberByHostInstance.version = '19.0.0';
    }
  };

  const registerInterfacePatch = () => {
    const orig = hook.registerRendererInterface;
    if (!orig || orig.__patched) return;
    hook.registerRendererInterface = function (renderer) {
      ensureRendererVersion(renderer);
      try {
        return orig.call(this, renderer);
      } catch (e) {
        if (e?.message?.includes('semver') || e?.message?.includes('Invalid argument')) return;
        throw e;
      }
    };
    hook.registerRendererInterface.__patched = true;
  };
  registerInterfacePatch();
  setTimeout(registerInterfacePatch, 0);
  setTimeout(registerInterfacePatch, 100);

  // Patch registerRenderer - ensures version before React's renderer reaches DevTools
  const originalRegisterRenderer = hook.registerRenderer;
  if (originalRegisterRenderer) {
    hook.registerRenderer = function (renderer) {
      if (renderer && (!renderer.version || String(renderer.version).trim() === '')) {
        renderer.version = '19.0.0';
      }
      try {
        return originalRegisterRenderer.call(this, renderer);
      } catch (error) {
        if (error && (error.message?.includes('semver') || error.message?.includes('Invalid argument'))) {
          return;
        }
        throw error;
      }
    };
  }
  
  // Override console.error to filter semver errors
  const originalConsoleError = (typeof console !== 'undefined' && typeof console.error === 'function') ? console.error.bind(console) : null;
  if (originalConsoleError) {
    console.error = function(...args) {
      const message = args.join(' ');
      if (message.includes('semver') && message.includes('Invalid argument')) {
        return; // Suppress
      }
      originalConsoleError.apply(console, args);
    };
  }

  // Intercept addEventListener to block semver errors from reaching error overlay
  const originalAddEventListener = EventTarget.prototype.addEventListener;
  EventTarget.prototype.addEventListener = function(type, listener, ...args) {
    if (type === 'error' && listener) {
      const wrappedListener = function(event) {
        const error = event.error || event.reason;
        if (error && (
          error.message?.includes('semver') ||
          error.message?.includes('Invalid argument') ||
          event.message?.includes('semver')
        )) {
          event.stopImmediatePropagation();
          event.preventDefault();
          return;
        }
        return listener.call(this, event);
      };
      return originalAddEventListener.call(this, type, wrappedListener, ...args);
    }
    return originalAddEventListener.call(this, type, listener, ...args);
  };
}

// Disable React error overlay to prevent DevTools semver errors from showing
if (process.env.NODE_ENV === 'development') {
  window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__ = {
    iframeRoot: null
  };
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import './utils/assetLoader'; // DRACO decoder path + texture caps
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { AppProvider } from './context/AppContext';
import { Toaster } from 'sonner';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <AppProvider>
        <BrowserRouter>
          <App />
          <Toaster richColors position="top-right" closeButton />
        </BrowserRouter>
      </AppProvider>
    </Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
